<script setup lang="ts">
import { ref } from 'vue';

import WalletButton from '@/components/web3/WalletButton.vue';
import { EXTERNAL_LINKS } from '@/constants/links';
import { SupportedWallets } from '@/providers/wallet.provider';
import LS_KEYS from '@/constants/local-storage.keys';

interface Props {
  isVisible?: boolean;
  onShowThirdParty: () => void;
}

type AcceptedLocalStorageItemType = '0' | '1' | null;

const props = withDefaults(defineProps<Props>(), {
  isVisible: false,
});

const emit = defineEmits(['close']);

const wallets = ref(SupportedWallets.filter(id => id !== 'safe'));

const acceptedlocalStorageItem = localStorage.getItem(
  LS_KEYS.App.TermsAccepted
) as AcceptedLocalStorageItemType;

const accepted = ref<'0' | '1'>(acceptedlocalStorageItem || '0');

const isBalRulesAccepted = computed(() => accepted.value === '1');

function onBalRulesAccepted() {
  accepted.value = isBalRulesAccepted.value ? '0' : '1';
  localStorage.setItem(LS_KEYS.App.TermsAccepted, accepted.value);
}
</script>

<template>
  <BalModal :show="props.isVisible" class="p-3" @close="emit('close')">
    <h2 class="mb-3 font-semibold dark:text-polaris-2">Connect to a wallet</h2>
    <BalRadio
      :checked="isBalRulesAccepted"
      value="bal-rules"
      name="bal-rules"
      size="lg"
      @update:model-value="onBalRulesAccepted"
    >
      <template #label>
        <p class="pb-3 pl-1 -mt-1 mb-2 text-base">
          {{ $t('byConnectingWallet') }}
          <router-link
            :to="{ name: 'terms-of-use' }"
            target="_blank"
            @click.stop=""
          >
            <span className="link">{{ $t('policies.termsOfUse') }} </span>,
          </router-link>
          <router-link :to="{ name: 'risks' }" target="_blank" @click.stop="">
            <span className="link">{{ $t('policies.risks') }} </span>,
          </router-link>
          <router-link
            :to="{ name: 'cookies-policy' }"
            target="_blank"
            @click.stop=""
          >
            <span className="link">
              {{ $t('policies.cookiesPolicy') }}
            </span> </router-link
          >,
          <span>{{ $t('useOf') }}&nbsp;</span>
          <button @click.stop="onShowThirdParty">
            <BalLink>
              <span>{{ $t('policies.thirdPartyServices') }}</span>
            </BalLink>
          </button>
          {{ $t('and') }}
          <router-link
            :to="{ name: 'privacy-policy' }"
            target="_blank"
            @click.stop=""
          >
            <span className="link">{{ $t('policies.privacyPolicy') }} </span>.
          </router-link>
        </p>
      </template>
    </BalRadio>

    <div
      :class="[
        !isBalRulesAccepted && 'grayscale pointer-events-none opacity-20',
        'transition-opacity duration-200',
      ]"
    >
      <WalletButton v-for="wallet in wallets" :key="wallet" :wallet="wallet" />
      <div class="p-4 mt-5 rounded-lg">
        <h6 class="font-semibold">{{ $t('newToEthereum') }}</h6>
        <p class="text-sm">
          {{ $t('setUpEthereumWallet') }}
          <BalLink :href="EXTERNAL_LINKS.Ethereum.Wallets" external>
            {{ $t('learnMore') }}
            <span class="align-middle">
              <BalIcon name="arrow-up-right" size="sm" />
            </span>
          </BalLink>
        </p>
      </div>
    </div>
  </BalModal>
</template>
